import React from 'react';

import PlayedPill from './PlayedPill';
import { CourseMinifiedDTO } from '../../services/apiClient';
import { courseLogo } from '../../utils/courseClosure';
import { formatDistance } from '../../utils/geoUtils';
import ClosedBadge from '../common/ClosedBadge';

interface CourseListRowProps {
  course: CourseMinifiedDTO;
  // Undefined in the A to Z list when the visitor's location is unknown: the row shows no distance.
  distanceMeters?: number;
  isSelected?: boolean;
  isOrigin?: boolean;
  isPlayed: boolean;
  onClick: () => void;
  onTogglePlayed: () => void;
}

// One course, one row. The dense shape: logo, name, address, distance, played.
//
// This is the list the client asked for, drawn off the Courses Played table in the mark-up:
// courses stacked hard against each other so a screen holds as many as it can. The card grid is
// the other view and keeps the hero photos; nothing here competes with it. The rule that decides
// what belongs on a row is "does it help you pick a course from a long list": a name, where it is,
// how far, and whether you have already been.
//
// Bucket list is deliberately absent. It lives on the card and in the detail drawer. A second
// toggle on a dense row is how the row stops being dense.
//
// An <article role="button"> rather than a real <button>, because the played pill is a form
// control and a button may not contain one. The keyboard handler is what a real button would have
// given for free.
const CourseListRow: React.FC<CourseListRowProps> = ({
  course,
  distanceMeters,
  isSelected = false,
  isOrigin = false,
  isPlayed,
  onClick,
  onTogglePlayed,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    // Space scrolls the list by default, which on a 41k list is a long way from where the member
    // was reading.
    event.preventDefault();
    onClick();
  };

  // A closed course shows the shared Closed artwork, a dark tile like the GolfPlayed mark, so it
  // goes without the white plate too.
  const { src: logoSrc, hasOwnLogo } = courseLogo(course);
  const distanceLabel = isOrigin
    ? 'This course'
    : distanceMeters != null
      ? formatDistance(distanceMeters)
      : null;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      // A hairline between rows and nothing else: no card border, no radius, no gap. The rows are
      // meant to read as one continuous list, which is what makes a screenful of them countable.
      className={`flex w-full cursor-pointer items-center gap-3 border-b border-white/10 px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:bg-white/10 sm:px-4 ${
        isSelected ? 'bg-primary/10' : 'hover:bg-white/[0.06]'
      }`}
    >
      {/* The white plate is only for a real club logo. The GolfPlayed fallback is dark artwork on
          a black tile (see CourseImageConstants), so on a white plate it floats in a white box. */}
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg ${
          hasOwnLogo ? 'bg-white p-1 ring-1 ring-black/20' : ''
        }`}
      >
        <img
          src={logoSrc}
          // Empty alt when the plate is showing a stand-in (the GolfPlayed mark, or Closed) rather
          // than this club's own logo: naming the club would describe an image that is not it.
          alt={hasOwnLogo ? (course.name ?? '') : ''}
          className="h-full w-full object-contain"
          draggable={false}
          loading="lazy"
        />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold leading-snug text-white">
          {course.name}
        </span>
        <span className="mt-0.5 flex min-w-0 items-baseline gap-1.5 text-xs leading-snug">
          {/* On a phone the distance rides with the address instead of holding a column of its
              own. Measured at 375px: the column plus the played pill left the name about 150px,
              which truncated "Simola Golf & Country Estate" at the word "Country". The name is
              the one thing a member is scanning for, so it gets the width back. */}
          <span className="shrink-0 font-semibold tabular-nums text-primary/80 sm:hidden">
            {distanceLabel}
          </span>
          <ClosedBadge
            closed={course.closed}
            closedYear={course.closedYear}
            closedMonth={course.closedMonth}
            className="self-center"
          />
          {course.address && (
            <span className="min-w-0 truncate text-white/40">
              {course.address}
            </span>
          )}
        </span>
      </span>

      {/* From sm up there is room for it to hold its own column, where it reads in the same place
          on every row and the list scans down. tabular-nums keeps the digits from shuffling as
          the numbers change width. */}
      <span className="hidden shrink-0 text-right text-[11px] font-semibold tabular-nums text-primary/80 sm:block">
        {distanceLabel}
      </span>

      <PlayedPill
        courseName={course.name ?? ''}
        isPlayed={isPlayed}
        onToggle={onTogglePlayed}
        className="shrink-0"
      />
    </article>
  );
};

export default CourseListRow;
